import { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Drawer, Empty, Space, Table, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";

import { attendanceApi } from "../api/attendance";
import type { AttendanceRecord, AttendanceSummaryRow } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { formatDateTimeIST } from "../utils/format";

const { RangePicker } = DatePicker;

/** Default window: the current month to date, which is how attendance is read. */
const defaultRange = (): [Dayjs, Dayjs] => [dayjs().startOf("month"), dayjs()];

export function Attendance() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange);
  const [rows, setRows] = useState<AttendanceSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFor, setOpenFor] = useState<AttendanceSummaryRow | null>(null);

  const params = {
    from: range[0].format("YYYY-MM-DD"),
    to: range[1].format("YYYY-MM-DD"),
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(
        await attendanceApi.summary({
          from: range[0].format("YYYY-MM-DD"),
          to: range[1].format("YYYY-MM-DD"),
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  // Working days elapsed in the window, so "12 of 20" reads sensibly rather
  // than comparing against every calendar day including weekends.
  const totalDays = range[1].diff(range[0], "day") + 1;

  const columns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      render: (v: string, row: AttendanceSummaryRow) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <div className="small-muted" style={{ fontSize: 12 }}>
            {row.employee_email}
          </div>
        </div>
      ),
    },
    {
      title: "Today",
      dataIndex: "present_today",
      width: 110,
      render: (v: boolean) =>
        v ? (
          <Tag color="green">Present</Tag>
        ) : (
          <Tag color="default">Not marked</Tag>
        ),
    },
    {
      title: "Days present",
      dataIndex: "days_present",
      width: 150,
      align: "right" as const,
      sorter: (a: AttendanceSummaryRow, b: AttendanceSummaryRow) =>
        a.days_present - b.days_present,
      render: (v: number) => (
        <span className="tabular">
          {v}
          <span className="small-muted"> / {totalDays}</span>
        </span>
      ),
    },
    {
      title: "First marked",
      dataIndex: "first_marked",
      width: 140,
      render: (v: string | null) =>
        v ? v : <span className="small-muted">—</span>,
    },
    {
      title: "Last marked",
      dataIndex: "last_marked",
      width: 140,
      render: (v: string | null) =>
        v ? v : <span className="small-muted">—</span>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 100,
      render: (v: boolean) =>
        v ? null : <Tag color="default">inactive</Tag>,
    },
    {
      title: "",
      width: 110,
      render: (_: unknown, row: AttendanceSummaryRow) => (
        <Button size="small" onClick={() => setOpenFor(row)}>
          View record
        </Button>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <PageHeader
        title="Attendance"
        subtitle="Days each employee marked themselves present"
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <Space>
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(v) => {
              if (v && v[0] && v[1]) setRange([v[0], v[1]]);
            }}
          />
          <Button
            onClick={() => setRange([dayjs().startOf("month"), dayjs()])}
          >
            This month
          </Button>
          <Button
            onClick={() =>
              setRange([
                dayjs().subtract(1, "month").startOf("month"),
                dayjs().subtract(1, "month").endOf("month"),
              ])
            }
          >
            Last month
          </Button>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load} />
      </div>

      <div className="card">
        <Table
          rowKey="employee_id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      </div>

      <RecordDrawer
        row={openFor}
        params={params}
        onClose={() => setOpenFor(null)}
      />
    </div>
  );
}

function RecordDrawer({
  row,
  params,
  onClose,
}: {
  row: AttendanceSummaryRow | null;
  params: Record<string, string>;
  onClose: () => void;
}) {
  const [record, setRecord] = useState<AttendanceRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [wholeHistory, setWholeHistory] = useState(false);

  useEffect(() => {
    if (!row) {
      setRecord(null);
      setWholeHistory(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Omitting the window asks the server for the employee's whole history
    // (capped at a year), rather than the range the table is showing.
    attendanceApi
      .record(row.employee_id, wholeHistory ? {} : params)
      .then((r) => {
        if (!cancelled) setRecord(r);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, wholeHistory, params.from, params.to]);

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={520}
      title={row ? `${row.employee_name} — attendance` : ""}
    >
      {row && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <div className="kpi-label">Days present</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {record?.days_present ?? "—"}
                <span
                  className="small-muted"
                  style={{ fontSize: 14, fontWeight: 400 }}
                >
                  {" "}
                  / {record?.days_in_range ?? "—"}
                </span>
              </div>
              {record && (
                <div className="small-muted" style={{ fontSize: 12 }}>
                  {record.from} to {record.to}
                </div>
              )}
            </div>
            <Button size="small" onClick={() => setWholeHistory((v) => !v)}>
              {wholeHistory ? "Selected range" : "Whole history"}
            </Button>
          </div>

          <Table
            rowKey="date"
            size="small"
            loading={loading}
            pagination={{ pageSize: 31, hideOnSinglePage: true }}
            locale={{ emptyText: <Empty description="No days in range" /> }}
            dataSource={record?.days ?? []}
            columns={[
              { title: "Date", dataIndex: "date", width: 130 },
              {
                title: "Status",
                dataIndex: "present",
                width: 110,
                render: (v: boolean) =>
                  v ? (
                    <Tag color="green">Present</Tag>
                  ) : (
                    <span className="small-muted">Not marked</span>
                  ),
              },
              {
                title: "Marked at",
                dataIndex: "marked_at",
                render: (v: string | null) =>
                  v ? (
                    <Tooltip title={formatDateTimeIST(v)}>
                      <span className="small-muted">
                        {formatDateTimeIST(v)}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="small-muted">—</span>
                  ),
              },
            ]}
          />
          <div
            className="small-muted"
            style={{ marginTop: 12, fontSize: 12 }}
          >
            "Not marked" means no attendance was recorded that day — it does
            not distinguish a day off, leave, or a holiday.
          </div>
        </>
      )}
    </Drawer>
  );
}
